import { motion } from 'motion/react';
import type { ReactNode } from 'react';

interface RevealStageProps {
  visible: boolean;
  children: ReactNode;
  /** Optional className passed straight onto the motion wrapper —
   *  lets callers preserve layout helpers (flex item sizing, etc.)
   *  without an extra div. */
  className?: string;
  /** Returning view — paint the block in its target state from the
   *  first paint (initial = current target) with a 0 s transition,
   *  so an already-visible block shows with no fade / lift / blur.
   *  Default false. */
  instant?: boolean;
}

const HIDDEN = { opacity: 0, y: 24, scale: 0.94, filter: 'blur(10px)' } as const;
const SHOWN = { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' } as const;

/**
 * Common entrance for every block on the winner screen. Children
 * stay mounted so their internal animations (typewriter / odometer)
 * can reserve layout from the first paint — the wrapper only
 * cross-fades opacity / lift / soft blur.
 */
export function RevealStage({
  visible,
  children,
  className,
  instant = false,
}: RevealStageProps) {
  return (
    <motion.div
      initial={instant ? (visible ? SHOWN : HIDDEN) : HIDDEN}
      animate={visible ? SHOWN : HIDDEN}
      transition={
        instant
          ? { duration: 0 }
          : { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
      }
      className={className}
    >
      {children}
    </motion.div>
  );
}
